import { Component, OnInit, inject, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject, Subject, debounceTime, distinctUntilChanged, take, map, filter } from 'rxjs';
import { toast } from '../../../../shared/utils/toast';
import { Store } from '@ngrx/store';
import { SettingsService } from '../../../../core/services/settings.service';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { selectBranchId } from '../../../../core/store/auth/auth.selectors';
import { environment } from '../../../../../environments/environment';
 
import { Popover } from 'primeng/popover';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';

@Component({
  selector: 'app-executables-tab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    AppPrimeTable,
    AppButton,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    Popover,
    HasPermissionDirective
  ],
  templateUrl: './executables-tab.html',
})
export class ExecutablesTab implements OnInit {
  private settingsService = inject(SettingsService);
  private translate = inject(TranslateService);
  private store = inject(Store);
  private perm = inject(PermissionService);

  get canCreate(): boolean { return this.perm.canCreate(MODULE.CONFIGURACOES); }
  get canEdit(): boolean { return this.perm.canEdit(MODULE.CONFIGURACOES); }
  get canDelete(): boolean { return this.perm.canDelete(MODULE.CONFIGURACOES); }

  @ViewChild('pathTemplate', { static: true }) pathTemplate!: TemplateRef<any>;
  @ViewChild('actionsTemplate', { static: true }) actionsTemplate!: TemplateRef<any>;

  templates: { [key: string]: TemplateRef<any> } = {};

  // Executable Columns
  executableColumns: PrimeTableColumn[] = [
    { field: 'executable_title', header: 'executables_table_title' },
    { field: 'executable_path', header: 'executables_table_path' },
    { field: 'actions', header: 'executables_table_actions', styleClass: 'text-center', type: 'action', width: '100px' }
  ];

  executables$ = new BehaviorSubject<any[]>([]);
  isLoadingExecutables = true;
  searchQuery = '';
  
  // Table state
  totalRecords = 0;
  rows = 10;
  first = 0;
  private searchSubject = new Subject<string>();

  // Modal States
  showAddExecutableModal = false;
  showEditExecutableModal = false;
  showDeleteConfirmModal = false;
  
  selectedExecutable: any = null;
  executableForm = {
    title: '',
    path: ''
  };
  formError = false;

  ngOnInit() {
    this.templates = {
      executable_path: this.pathTemplate,
      actions: this.actionsTemplate
    };

    this.loadExecutables();
    
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(() => {
      this.first = 0;
      this.loadExecutables();
    });
  }

  loadExecutables() {
    this.isLoadingExecutables = true;
    this.settingsService.getExecutables(this.searchQuery, this.rows, this.first).subscribe({
      next: (data: any) => {
        this.executables$.next(data);
        // Assuming data length and common meta logic for total Records
        // Since getExecutables might not return total count, we handle it simply
        this.totalRecords = data.length === this.rows ? this.first + this.rows + 1 : this.first + data.length;
        this.isLoadingExecutables = false;
      },
      error: () => (this.isLoadingExecutables = false)
    });
  }

  onSearchChange() {
    this.searchSubject.next(this.searchQuery);
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    this.loadExecutables();
  }


  get isElectron(): boolean {
    return window.navigator.userAgent.toLowerCase().includes('electron');
  }

  downloadInstaller(platform: 'windows' | 'mac' = 'windows') {
    this.settingsService.getInstallerSignedUrl(platform).subscribe({
      next: (url) => {
        window.open(url, '_blank');
      },
      error: (err) => {
        console.error('Error fetching installer signed URL:', err);
        const msg = this.translate.instant('executables_installer_not_found');
        toast.error(msg);
      }
    });
  }

  downloadGovernmentMiddleware() {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isMac = userAgent.indexOf('mac') !== -1;
    const url = isMac ? environment.govMiddlewareUrlMac : environment.govMiddlewareUrlWindows;
    window.open(url, '_blank');
  }

  openAddModal() {
    if (!this.canCreate) return;
    this.executableForm = { title: '', path: '' };
    this.formError = false;
    this.showAddExecutableModal = true;
  }

  openEditModal(executable: any) {
    if (!this.canEdit) return;
    this.selectedExecutable = executable;
    this.executableForm = {
      title: executable.executable_title,
      path: executable.executable_path
    };
    this.formError = false;
    this.showEditExecutableModal = true;
  }

  openDeleteModal(executable: any) {
    if (!this.canDelete) return;
    this.selectedExecutable = executable;
    this.showDeleteConfirmModal = true;
  }

  closeModals() {
    this.showAddExecutableModal = false;
    this.showEditExecutableModal = false;
    this.showDeleteConfirmModal = false;
    this.selectedExecutable = null;
    this.formError = false;
  }

  saveExecutable() {
    if (!this.canCreate) return;
    if (!this.executableForm.title || !this.executableForm.path) {
      this.formError = true;
      return;
    }

    this.store.select(selectBranchId).pipe(filter((id): id is number => !!id), take(1)).subscribe((branchId) => {
      this.settingsService.createExecutable(
        this.executableForm.title,
        this.executableForm.path,
        branchId
      ).subscribe({
        next: () => {
          this.closeModals();
          this.loadExecutables();
        },
        error: (err) => console.error('Error creating executable:', err)
      });
    });
  }

  updateExecutable() {
    if (!this.canEdit) return;
    if (!this.executableForm.title || !this.executableForm.path || !this.selectedExecutable) {
      this.formError = true;
      return;
    }

    this.settingsService.updateExecutable(
      this.selectedExecutable.id,
      this.executableForm.title,
      this.executableForm.path
    ).subscribe({
      next: () => {
        this.closeModals();
        this.loadExecutables();
      },
      error: (err) => console.error('Error updating executable:', err)
    });
  }

  deleteExecutable() {
    if (!this.canDelete) return;
    if (!this.selectedExecutable) return;

    this.settingsService.deleteExecutable(this.selectedExecutable.id).subscribe({
      next: () => {
        this.closeModals();
        this.loadExecutables();
      },
      error: (err) => console.error('Error deleting executable:', err)
    });
  }

  runExecutable(path: string) {
    alert(this.translate.instant('executables_desktop_only') + '\nPath: ' + path);
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(this.translate.instant('common_copied'));
    });
  }
}
